import { connect } from 'react-redux';
import { compose, map, noop } from 'lodash/fp';
import { createStructuredSelector } from 'reselect';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    ContentCard,
    ConvertedCurrencyValue,
    DefaultErrorBoundary,
    LocalizedDateTimeFromUtc,
} from '@mews-commander/ui';
import { Button } from '@optimus-web/core';
import { ColumnAlignment } from '@mews/b2b-ui';
import { ApplicationState, OrderItem, CommanderThunkDispatch } from '@mews-commander/types';
import { sortByAscending, localizeText } from '@mews-commander/utils';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { Table, TableWrapperElement } from '@mews-commander/ui/legacy';
import { createDeposit, cancelOrderItems } from '../../actions';
import { getSortedReservationsAdditionalExpenses } from '../../selectors/orderItems';
import { withActiveWindowLoader } from '../../../../core';
import { BillLink } from '../../../../components';
import { additionalExpensesHandler } from '../additionalExpensesHandler';
import { CancelButton } from './CancelButton';

const tableMapping = [
    {
        title: <LocalizedText textKey={TextKey.ExpenseType} />,
        valueKey: 'type',
        columnWidth: '20%',
        render: ({ data, serviceId }: TableSourceRow) => additionalExpensesHandler(data.discriminator, { serviceId }),
    },
    {
        title: <LocalizedText textKey={TextKey.Cost} />,
        valueKey: 'cost',
        columnAlignment: ColumnAlignment.Right,
        columnWidth: '20%',
        render: ({ totalAmount, isMultiTaxEnvironment }: TableSourceRow) => (
            <ConvertedCurrencyValue amount={totalAmount} net={isMultiTaxEnvironment} />
        ),
    },
    {
        title: <LocalizedText textKey={TextKey.Consumed} />,
        valueKey: 'consumed',
        columnWidth: '25%',
        render: ({ consumedUtc }: TableSourceRow) =>
            consumedUtc ? <LocalizedDateTimeFromUtc utcString={consumedUtc} /> : null,
    },
    {
        title: <LocalizedText textKey={TextKey.Bill} />,
        valueKey: 'bill',
        render: ({ id, groupId }: TableSourceRow) => <BillLink orderItemId={id} groupId={groupId} />,
    },
    {
        title: ' ',
        valueKey: 'action',
        columnAlignment: ColumnAlignment.Right,
        render: ({ isCanceled, handleCancel }: TableSourceRow) => (
            <CancelButton isCancelled={isCanceled} handleCancel={handleCancel} />
        ),
    },
];

const AdditionalExpenses = ({
    serviceId,
    additionalExpenses,
    isMultiTaxEnvironment,
    canReservationAcceptNewDeposit,
    cancelAdditionalExpense,
    openNewDepositModal,
    groupId,
}: Props) => {
    const tableSource: TableSourceRow[] = compose(
        map((expense: OrderItem) => ({
            ...expense,
            serviceId,
            isMultiTaxEnvironment,
            groupId,
            handleCancel: () => {
                cancelAdditionalExpense(expense.id);
            },
        })),
        sortByAscending<OrderItem>('consumedUtc')
    )(additionalExpenses);

    return (
        <DefaultErrorBoundary>
            <ContentCard
                title={<LocalizedText textKey={TextKey.AdditionalExpensePlural} />}
                actions={
                    canReservationAcceptNewDeposit && (
                        <Button onClick={openNewDepositModal} data-test-new-deposit-button type="submit">
                            <LocalizedText textKey={TextKey.NewDeposit} />
                        </Button>
                    )
                }
                data-test-additional-expenses-table
            >
                <TableWrapperElement>
                    <Table mapping={tableMapping} source={tableSource} />
                </TableWrapperElement>
            </ContentCard>
        </DefaultErrorBoundary>
    );
};

interface StateProps {
    additionalExpenses: OrderItem[];
    isMultiTaxEnvironment: boolean;
}
interface DispatchProps {
    cancelAdditionalExpense: (id: string) => void;
    openNewDepositModal: () => void;
}
interface OwnProps {
    reservationId: string;
    groupId: string;
    accountId: string;
    refetchGroup(): void;
    canReservationAcceptNewDeposit: boolean;
    serviceId: string;
}
type Props = StateProps & DispatchProps & OwnProps;

interface TableSourceRow extends OrderItem {
    serviceId: string;
    groupId: string;
    handleCancel: () => void;
    isMultiTaxEnvironment: boolean;
}

const connectAdditionalExpenses = connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    createStructuredSelector({
        isMultiTaxEnvironment: (state) => isCurrentEnterpriseInMultiTaxEnvironment(state),
        additionalExpenses: (state: ApplicationState, { reservationId }: OwnProps) =>
            getSortedReservationsAdditionalExpenses(state, { serviceOrderIds: [reservationId] }),
    }),
    (dispatch: CommanderThunkDispatch, { reservationId, refetchGroup = noop }: OwnProps) => ({
        cancelAdditionalExpense: async (additionalExpenseId: string) =>
            withActiveWindowLoader(async () => dispatch(cancelOrderItems([additionalExpenseId]))).then(() => {
                refetchGroup();
                showSuccessAlert({
                    description: lt('CancelEntitySuccessMessage', { entity: localizeText(TextKey.AdditionalExpense) }),
                });
            }),
        openNewDepositModal: () => dispatch(createDeposit({ reservationId })),
    })
);

export default connectAdditionalExpenses(AdditionalExpenses);
